import React, { useEffect, useState } from "react";
import websiteSettingsIllustration from "../../assets/illustrationwebmobile.png";
import axios from "axios";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../../hooks/useAuthContext";
import ManageSubscriptions from "../modals/ManageSubscriptions";
import "./MyOneWebsite.css"

export default function MyOneWebsite() {
  const [website, setWebsite] = useState(null);
  const [offer, setOffer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showManageModal, setShowManageModal] = useState(false);

  const { id } = useParams();
  const { user } = useAuthContext();

  const activateCheckout = async () => {
    if (!offer) return;

    try {
      const checkoutResponse = await axios.post(
        `${ApiConnectionString}/api/checkout/${id}/${offer.priceId}`,
        {},
        {
          headers: { Authorization: `bearer ${user.token}` },
        }
      );

      const url =
        typeof checkoutResponse.data === "string"
          ? checkoutResponse.data
          : checkoutResponse.data?.url || checkoutResponse.data?.Url;

      if (url) {
        window.location.href = url;
      } else {
        console.log("No url found in response");
      }
    } catch (error) {
      console.log("Checkout error:", error);
    }
  };

  const toDate = (value) => {
    if (!value) return null;
    if (typeof value === "string") return new Date(value);
    if (typeof value === "number") {
      const str = value.toString();
      if (str.length === 10) return new Date(value * 1000);
      return new Date(value);
    }
    return null;
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const websiteResponse = await axios.get(
        `${ApiConnectionString}/api/website/${id}`,
        {
          headers: { Authorization: `bearer ${user.token}` },
        }
      );

      if (websiteResponse.status === 200) {
        const data = websiteResponse.data;
        setWebsite(data);

        if (data.activeSubScription === false && data.offerId != null) {
          const offerResponse = await axios.get(
            `${ApiConnectionString}/api/offer/${data.offerId}`,
            {
              headers: { Authorization: `bearer ${user.token}` },
            }
          );

          setOffer(offerResponse.data);
        }

        if (data.activeSubScription === true) {
          const subResponse = await axios.get(
            `${ApiConnectionString}/api/subscription/${id}`,
            {
              headers: { Authorization: `bearer ${user.token}` },
            }
          );

          if (subResponse.status === 200) {
            setSubscription(subResponse.data);
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  if (isLoading || !website) {
    return (
      <div className="container mt-5">
        <p>Loading</p>
      </div>
    );
  }

  const createdDate = website.created
    ? new Date(website.created).toLocaleDateString()
    : "";

  const currencySymbols = {
    gbp: "£",
    usd: "$",
    eur: "€",
    cad: "$",
    aud: "$",
  };

  const subCurrency =
    subscription && subscription.priceCurrency
      ? currencySymbols[subscription.priceCurrency.toLowerCase()] || ""
      : "";

  const subAmount =
    subscription && subscription.priceUnitAmount != null
      ? (subscription.priceUnitAmount / 100).toFixed(2)
      : null;

  const nextPaymentDate =
    subscription &&
    toDate(subscription.nextPayment || subscription.currentPeriodEnd);

  return (
    <div className="container mt-5 pt-4">
      <section className="mb-5">
        <div className="row align-items-center g-5">
          <div className="col-md-6 text-center text-md-start">
            <h1 className="fw-bold display-5 mb-3">Website settings</h1>
            <p className="text-muted fs-5 mb-4">
              Manage and configure this website. Open the editor, change the
              domain, and view other options.
            </p>

            {!website.developerReview && (
              <div className="alert alert-warning">
                <strong>Developer review pending</strong>
                <br />
                A developer is reviewing your concept. You will be contacted
                soon with a preview and a quote.
              </div>
            )}

            {!website.activeSubScription && offer && (
              <div className="alert alert-info rounded-4 p-4 mt-4">
                <h5 className="fw-bold mb-2">Subscription required</h5>

                <p className="mb-1">
                  <strong>Offer:</strong> {offer.productName}
                </p>

                <p className="mb-3">{offer.productDescription}</p>

                <p className="mb-3">
                  A developer has reviewed this website and offered a price of{" "}
                  <strong>{offer.displayPrice}</strong> to activate it. Your
                  first month will be free.
                </p>

                <div className="d-flex flex-wrap gap-2">
                  {offer.previewUrl && (
                    <a
                      href={offer.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-secondary"
                    >
                      View preview
                    </a>
                  )}

                  <button
                    onClick={activateCheckout}
                    className="btn brand-purple-inline text-white"
                  >
                    Start subscription
                  </button>
                </div>
              </div>
            )}

            {website.activeSubScription && subscription && (
              <div className="alert alert-success rounded-4 p-4 mt-4">
                <h5 className="fw-bold mb-2">Subscription</h5>

                <p className="mb-1">
                  <strong>Status:</strong> {subscription.status}
                </p>

                <p className="mb-1">
                  <strong>Cancel status:</strong>{" "}
                  {subscription.canceled
                    ? "Canceled"
                    : subscription.cancelPending
                    ? "Cancel pending at period end"
                    : "Active"}
                </p>

                {!subscription.canceled && (
                  <p className="mb-1">
                    <strong>Current period:</strong>{" "}
                    {subscription.currentPeriodStart
                      ? toDate(
                          subscription.currentPeriodStart
                        ).toLocaleDateString()
                      : ""}
                    {" to "}
                    {subscription.currentPeriodEnd
                      ? toDate(
                          subscription.currentPeriodEnd
                        ).toLocaleDateString()
                      : ""}
                  </p>
                )}

                {!subscription.canceled && !subscription.cancelPending && (
                  <p className="mb-1">
                    <strong>
                      {subscription.status === "trialing"
                        ? "Trial ends on:"
                        : "Next payment:"}
                    </strong>{" "}
                    {nextPaymentDate
                      ? nextPaymentDate.toLocaleString()
                      : "No next payment"}
                  </p>
                )}

                <button
                  type="button"
                  className="btn btn-secondary mt-3"
                  onClick={() => setShowManageModal(true)}
                >
                  Manage subscription
                </button>
              </div>
            )}

            <div className="custom-card-light p-4 rounded-4 mb-4">
              <h5 className="fw-bold mb-3">Website information</h5>

              <p className="mb-1">
                <strong>Name:</strong> {website.name}
              </p>

              <p className="mb-1">
                <strong>Created:</strong> {createdDate}
              </p>

              <p className="mb-1">
                <strong>Domain:</strong>{" "}
                {website.domain ? website.domain : "No domain set"}
              </p>

              <p className="mb-1">
                <strong>Live:</strong>{" "}
                {website.live ? (
                  <span className="text-success">Published</span>
                ) : (
                  <span className="text-danger">Not published</span>
                )}
              </p>
            </div>

            {!website.developerReview && (
              <div className="custom-card-light p-4 rounded-4 mb-4">
                <h5 className="fw-bold mb-3">Concept</h5>
                <p className="mb-0">{website.concept}</p>
              </div>
            )}

            <div className="d-flex flex-wrap gap-3">
          

              {website.live && (
                <a
                  href={`https://${website.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn brand-purple-outline"
                >
                  View live site
                </a>
              )}

              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled
              >
                View analytics (coming soon)
              </button>
            </div>
          </div>

          <div className="col-md-6 text-center">
            <img
              src={websiteSettingsIllustration}
              alt="Website settings illustration"
              className="img-fluid illustration-img"
            />
          </div>
        </div>
      </section>



      {showManageModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <ManageSubscriptions
                user={user}
                onClose={() => setShowManageModal(false)}
                onSave={async (e) => {
                  setShowManageModal(false);

                  const response = await axios.get(
                    `${ApiConnectionString}/api/BillingPortal`,
                    {
                      headers: { Authorization: `bearer ${user.token}` },
                    }
                  );

                  window.location.href = response.data;
                }}
              />
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}
